import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DownloadIcon from '../../assets/icons/download.svg?react';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import { useAuth } from '../../hooks/useAuth';
import { fetchReportPreviewHtml } from './utils/reportPreviewHtml';

const ReportPreview = () => {
  const { reportRequestSysId } = useParams();
  const navigate = useNavigate();
  const [iframeError, setIframeError] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [iframeHeight, setIframeHeight] = useState(1131);
  const iframeRef = useRef(null);
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const { user } = useAuth();
  // iframe 높이 계산
  useEffect(() => {
    const calculateIframeHeight = () => {
      if (contentRef.current && headerRef.current) {
        const contentHeight = contentRef.current.clientHeight;
        const headerHeight = headerRef.current.offsetHeight;
        const calculatedHeight = contentHeight - headerHeight;
        // A4 비율 유지 (800px 기준으로 계산)
        const a4Ratio = 297 / 210; // A4 세로/가로 비율
        const maxHeight = 800 * a4Ratio; // 1131px
        setIframeHeight(Math.min(calculatedHeight, maxHeight));
      }
    };

    calculateIframeHeight();
    window.addEventListener('resize', calculateIframeHeight);
    
    return () => {
      window.removeEventListener('resize', calculateIframeHeight);
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'iframe-error') {
        setIframeError(true);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    if (!reportRequestSysId) {
      setPreviewLoading(false);
      setIframeError(true);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setIframeError(false);
    setPreviewHtml(null);

    (async () => {
      try {
        const html = await fetchReportPreviewHtml(reportRequestSysId);
        if (!cancelled) {
          setPreviewHtml(html);
        }
      } catch (e) {
        console.error('보고서 미리보기 로드 실패:', e);
        if (!cancelled) {
          setIframeError(true);
          setPreviewHtml(null);
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reportRequestSysId]);

  const handleIframeLoad = () => {
    setIframeError(false);
    
    // iframe 로드 후 내부의 에러 확인 시도
    try {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        // iframe 내부의 리소스 로드 실패 확인 (제한적)
        // 실제로는 iframe 내부의 HTML에 스크립트 태그가 포함되어 있어도
        // 외부에서 이를 제거하기는 어렵습니다.
      }
    } catch (e) {
      // CORS 제한으로 접근 불가
    }
  };
  
  const handleIframeError = () => {
    setIframeError(true);
  };

  const handleDownload = () => {
    // 다운로드 기능 구현
    window.open(`/doc-management/api/output/download-pdf?reportRequestId=${reportRequestSysId}&downloadRequestId=${user.UserSysId}`, '_blank');
  };

  return (
    <div className="detail-page-container">
      <div className="detail-page-content" ref={contentRef}>
        <div ref={headerRef} className="flex align-center justify-between gap-5 my-5 px-5">
          <button 
            className="btn icon-btn flex align-center gap-2" 
            onClick={() => navigate('/report-request-management')}
          >
            <ChevronLeftIcon color="var(--gray-600)" />
            <h3 className="text-b2-700 text-gray-600">목록으로 돌아가기</h3>
          </button>
          <button className="btn small-btn btn-primary" onClick={handleDownload}>
            <DownloadIcon color="var(--white)" />
            다운로드
          </button>
        </div>
        
        <div style={{ width: '100%', flex: 1, height: '100%' }}>
          {previewLoading ? (
            <div className="flex flex-col align-center justify-center" style={{ height: '100%', gap: '12px' }}>
              <p className="text-b2-400 text-gray-600">보고서를 불러오는 중…</p>
            </div>
          ) : iframeError ? (
            <div className="flex flex-col align-center justify-center" style={{ height: '100%', gap: '12px' }}>
              <p className="text-b2-400 text-gray-600">보고서를 불러올 수 없습니다.</p>
              <p className="text-c2-400 text-gray-500">
                /doc-management/api/output/view?reportRequestId={reportRequestSysId}
              </p>
              <p className="text-c2-400 text-gray-400" style={{ fontSize: '12px', marginTop: '8px' }}>
                참고: 보고서 HTML에 포함된 외부 리소스는 별도로 로드되지 않을 수 있습니다.
              </p>
            </div>
          ) : (
            <div className="w-full flex align-center justify-center" style={{ height: '100%' }}>
              <iframe
                ref={iframeRef}
                key={reportRequestSysId}
                srcDoc={previewHtml ?? ''}
                width="100%"
                height="100%"
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                style={{ border: 'none', minHeight: iframeHeight }}
                sandbox="allow-same-origin allow-scripts allow-forms"
                title="보고서 미리보기"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportPreview;

